// Per-position health radar — Simply Wall St's "snowflake" pattern, retuned
// for a discipline-first dashboard. Five axes:
//   - Size discipline  (1 - position % of account / cap)
//   - Stop coverage    (binary: protected? 100 : 0)
//   - Sector spread    (1 - your sector's share / cap)
//   - Cost vs price    (where you are relative to entry, capped)
//   - Conviction       (RSI distance from extremes; 50 = max)
//
// All axes normalised to 0-100; bigger = healthier.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { api, type PositionWithTv } from '@/lib/api';

type Props = {
  position: PositionWithTv;
  totalAccountValue: number;
};

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n));
}

export function SnowflakeRadar({ position, totalAccountValue }: Props) {
  const orders = useQuery({ queryKey: ['active-orders'], queryFn: api.activeOrders });
  const ind = useQuery({
    queryKey: ['indicator', position.instrument.ticker],
    queryFn: () => api.indicator(position.instrument.ticker),
  });
  const positions = useQuery({ queryKey: ['positions'], queryFn: api.positions });

  const data = useMemo(() => {
    const SIZE_CAP = 10;
    const SECTOR_CAP = 30;

    const pctOfAccount =
      totalAccountValue > 0 ? (position.walletImpact.currentValue / totalAccountValue) * 100 : 0;
    const sizeScore = clamp(100 * (1 - Math.max(0, pctOfAccount - SIZE_CAP) / SIZE_CAP));

    const hasStop = (orders.data ?? []).some(
      (o) =>
        o.ticker === position.instrument.ticker &&
        (o.type === 'STOP' || o.type === 'STOP_LIMIT') &&
        o.side === 'SELL',
    );
    const stopScore = hasStop ? 100 : 0;

    let sectorScore = 100;
    const sector = position.instrument.sector;
    if (sector && (positions.data ?? []).length > 0) {
      const total = (positions.data ?? []).reduce(
        (a, p) => a + p.walletImpact.currentValue,
        0,
      );
      const sectorVal = (positions.data ?? [])
        .filter((p) => p.instrument.sector === sector)
        .reduce((a, p) => a + p.walletImpact.currentValue, 0);
      const sectorPct = total > 0 ? (sectorVal / total) * 100 : 0;
      sectorScore = clamp(100 * (1 - Math.max(0, sectorPct - SECTOR_CAP) / SECTOR_CAP));
    }

    const pnlPct =
      (position.walletImpact.unrealizedProfitLoss / Math.max(0.01, position.walletImpact.totalCost)) * 100;
    // 0% PnL = neutral; +30% caps at 100; -30% caps at 0.
    const costScore = clamp(50 + pnlPct * 1.67);

    const rsi = ind.data?.rsi14 ?? 50;
    const distFrom50 = Math.abs(rsi - 50);
    // 50 RSI = max conviction (100); 30 or 70 = 0.
    const convictionScore = clamp(100 - distFrom50 * 5);

    return [
      { axis: 'Size', score: Math.round(sizeScore) },
      { axis: 'Stop', score: Math.round(stopScore) },
      { axis: 'Sector', score: Math.round(sectorScore) },
      { axis: 'Cost', score: Math.round(costScore) },
      { axis: 'Conviction', score: Math.round(convictionScore) },
    ];
  }, [position, totalAccountValue, orders.data, positions.data, ind.data]);

  const avg = Math.round(data.reduce((a, b) => a + b.score, 0) / data.length);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-medium text-zinc-200">Health</h3>
        <span className="text-[11px] text-zinc-400">avg {avg}/100</span>
      </div>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 4, bottom: 4, left: 4, right: 4 }}>
            <PolarGrid stroke="rgba(63, 63, 70, 0.6)" />
            <PolarAngleAxis
              dataKey="axis"
              tick={{ fill: '#a1a1aa', fontSize: 11 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />
            <Radar
              name="Health"
              dataKey="score"
              stroke="#818cf8"
              fill="#6366f1"
              fillOpacity={0.4}
              isAnimationActive
              animationDuration={400}
            />
            <Tooltip
              contentStyle={{
                background: '#0a0a0f',
                border: '1px solid #26262f',
                borderRadius: 8,
                fontSize: 11,
              }}
              itemStyle={{ color: '#e4e4e7' }}
              formatter={(v: number) => [`${v}/100`, 'Score']}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
